<template>
  <div class="kanbanGroupOperations" v-if="enabled">
    <div class="d-flex align-center">
      <div class="kanbanGroupOperations__selected counter d-flex align-center">
        <div class="kanbanGroupOperations__counter d-flex align-center">
          <span class="kanbanGroupOperations__counter_selected">{{ $t('selected') }}: </span>
          <span>{{ selectedCandidates.length }}</span>
        </div>

        <V-icon class="ml-1" small @click="clearSelected">
          mdi-close
        </V-icon>
      </div>

      <div class="kanbanGroupOperations__btn-list">
        <VBtn
          v-for="(item, index) in items"
          :key="index"
          class="custom--btn"
          @click="setAction(item.value)"
        >
          {{ $t(item.text) }}
        </VBtn>
      </div>
    </div>

    <AppDialog
      v-if="selectedAction"
      v-model="opened"
      width="600"
      :hide-close-button="notifyLoading"
      persistent
    >
      <VCard class="pa-8 dialog">
        <template v-if="!notifyLoading">
          <VCardTitle>
            {{ $t(selectedAction === groupKanbanOperations.REJECT ? 'move' : selectedAction) }}
          </VCardTitle>

          <VCardText class="mt-6">
            <template v-if="!localLoading">
              <KanbanGroupOperationsCandidates />

              <div
                class="mb-2 mt-6"
                v-if="
                  [groupKanbanOperations.MOVE, groupKanbanOperations.REJECT].includes(selectedAction)
                "
              >
                <div class="mt-6">
                  {{ $t('changeStatus') }}
                </div>

                <AppCandidateStatusesSelect v-model="selectedStatus" />

                <v-checkbox
                  :disabled="!selectedStatus"
                  v-model="sendEmail"
                  hide-details
                  :label="$t('send_email')"
                />

                <SendMessageWithTemplateForm
                  v-if="sendEmail"
                  :key="`move-email-${selectedAction}`"
                  v-model="emailMessage"
                  class="mt-2"
                />

                <VContainer ma-0 pa-0 v-if="selectedStatus !== candidateStatus.DECLINED">
                  <VRow align="center" dense>
                    <VCol cols="12">
                      <v-checkbox
                        :disabled="!selectedStatus"
                        v-model="sendSms"
                        hide-details
                        :label="$t('send_sms')"
                      />
                    </VCol>
                    <template v-if="sendSms && selectedStatus !== candidateStatus.DECLINED">
                      <VCol cols="12">
                        <KanbanGroupOperationsQuota />
                      </VCol>
                      <VCol cols="12">
                        <SendMessageWithTemplateForm
                          :key="`move-sms-${selectedAction}`"
                          :channel="notificationChannelType.SMS"
                          v-model="smsMessage"
                        />
                      </VCol>
                    </template>
                  </VRow>
                </VContainer>
              </div>

              <div class="mt-6 mb-2" v-if="selectedAction === groupKanbanOperations.SEND_EMAIL">
                <SendMessageWithTemplateForm
                  :key="`send-email-${selectedAction}`"
                  v-model="emailMessage"
                />
              </div>

              <div class="mt-6 mb-2" v-if="selectedAction === groupKanbanOperations.SEND_SMS">
                <KanbanGroupOperationsQuota />

                <SendMessageWithTemplateForm
                  :key="`send-sms-${selectedAction}`"
                  :channel="notificationChannelType.SMS"
                  v-model="smsMessage"
                />
              </div>
              <VCardActions class="ml-0 pl-0 mt-2">
                <VBtn
                  color="success"
                  v-if="canApply"
                  @click="apply"
                  x-large
                  class="mr-2 px-8"
                >
                  {{ $t(selectedAction === groupKanbanOperations.REJECT ? 'move' : selectedAction) }}
                </VBtn>
                <VBtn
                  color="grey"
                  v-if="canApply"
                  @click="toggleOpen"
                  x-large
                  outlined
                  class="px-8"
                >
                  {{ $t('cancel') }}
                </VBtn>
              </VCardActions>
            </template>
          </VCardText>
        </template>
        <template v-else>
          <div class="kanban-group-operations__sending">
            <Preloader />
            <p class="kanban-group-operations__sending-text">
              {{ $t(sendingInProgressKey) }}
            </p>
          </div>
        </template>
      </VCard>
    </AppDialog>

    <VBtn v-if="selectedAction" @click="toggleOpen" class="mx-3">
      {{ $t('apply') }}
    </VBtn>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n-composable';
import { useKanbanMultipleOperations } from '@/compositions/useKanbanMultipleOperations';
import AppCandidateStatusesSelect from '@/components/AppCandidateStatusesSelect';
import { candidateStatus, groupKanbanOperations, notificationChannelType } from '@/enums';
import {
  computed, ref, watch,
} from '@vue/composition-api';
import AppDialog from '@/components/AppDialog';
import SendMessageWithTemplateForm from '@/forms/SendMessageWithTemplateForm';
import KanbanGroupOperationsCandidates from '@/components/Vacancy/Kanban/KanbanGroupOperationsCandidates';
import KanbanGroupOperationsQuota from '@/components/Vacancy/Kanban/KanbanGroupOperationsQuota';
import Preloader from '@/components/Preloader.vue';

export default {
  name: 'KanbanGroupOperations',
  components: {
    Preloader,
    KanbanGroupOperationsQuota,
    KanbanGroupOperationsCandidates,
    SendMessageWithTemplateForm,
    AppDialog,
    AppCandidateStatusesSelect,
  },
  setup(_, { emit }) {
    const {
      enabled,
      canSendSms,
      applyAction,
      localLoading,
      selectedCandidates,
      clearSelected,
    } = useKanbanMultipleOperations();
    const { t } = useI18n();
    const selectedAction = ref(null);
    const selectedStatus = ref(null);
    const opened = ref(false);
    const emailMessage = ref('');
    const smsMessage = ref('');
    const sendSms = ref(false);
    const sendEmail = ref(false);
    const notifyLoading = ref(false);

    watch(selectedAction, () => {
      if (selectedAction.value === groupKanbanOperations.REJECT) {
        selectedStatus.value = candidateStatus.DECLINED;
        selectedAction.value = groupKanbanOperations.MOVE;
      }
    });

    function toggleOpen() {
      opened.value = !opened.value;
    }

    async function apply() {
      try {
        emit('enableOperationLoader');
        notifyLoading.value = true;
        const actions = [];

        if (selectedAction.value === groupKanbanOperations.MOVE) {
          actions.push(
            applyAction({
              type: selectedAction.value,
              payload: { status: selectedStatus.value },
            }),
          );
          selectedStatus.value = null;

          if (sendSms.value && smsMessage.value) {
            actions.push(
              applyAction({
                type: groupKanbanOperations.SEND_SMS,
                payload: { template: smsMessage.value },
              }),
            );
          }
          if (sendEmail.value && emailMessage.value) {
            actions.push(
              applyAction({
                type: groupKanbanOperations.SEND_EMAIL,
                payload: { template: emailMessage.value },
              }),
            );
          }
        } else if (selectedAction.value === groupKanbanOperations.SEND_EMAIL) {
          actions.push(
            applyAction({
              type: selectedAction.value,
              payload: { template: emailMessage.value },
            }),
          );
          emailMessage.value = null;
        } else if (selectedAction.value === groupKanbanOperations.SEND_SMS) {
          actions.push(
            applyAction({
              type: selectedAction.value,
              payload: { template: smsMessage.value },
            }),
          );
          smsMessage.value = null;
        }

        await Promise.all(actions);
        const wasSendingMessages = [
          groupKanbanOperations.SEND_EMAIL,
          groupKanbanOperations.SEND_SMS,
        ].includes(selectedAction.value)
          || (selectedAction.value === groupKanbanOperations.MOVE && (sendEmail.value || sendSms.value));
        if (wasSendingMessages) {
          const message = t('messagesSent');
          emit('messagesSent', message);
        }
        emit('getPaginatedCandidates', {
          page: 1,
          perPage: 20,
        });
        emit('getCountCandidates');
      } catch (e) {
      } finally {
        emit('disableOperationLoader');
        notifyLoading.value = false;
        selectedAction.value = null;
        clearSelected();
        toggleOpen();
      }
    }

    const items = [
      {
        text: t('move'),
        value: groupKanbanOperations.MOVE,
      },
      {
        text: t('send_email'),
        value: groupKanbanOperations.SEND_EMAIL,
      },
      {
        text: t('send_sms'),
        value: groupKanbanOperations.SEND_SMS,
      },
      {
        text: t('reject'),
        value: groupKanbanOperations.REJECT,
      },
    ];

    function setAction(action) {
      selectedAction.value = action;
      toggleOpen();
    }

    const sendingInProgressKey = computed(() => {
      switch (selectedAction.value) {
        case groupKanbanOperations.SEND_SMS:
          return 'sendingSmsInProgress';
        case groupKanbanOperations.SEND_EMAIL:
          return 'sendingEmailInProgress';
        case groupKanbanOperations.MOVE:
          if (sendEmail.value && sendSms.value) return 'sendingMessagesInProgress';
          if (sendSms.value) return 'sendingSmsInProgress';
          if (sendEmail.value) return 'sendingEmailInProgress';
          return 'sendingMessagesInProgress';
        default:
          return 'sendingMessagesInProgress';
      }
    });

    return {
      items,
      enabled,
      apply,
      sendingInProgressKey,
      canApply: computed(() => {
        if (sendSms.value && !canSendSms.value) {
          return false;
        }

        if (selectedAction.value === groupKanbanOperations.MOVE) {
          return !!selectedStatus.value;
        }
        if (selectedAction.value === groupKanbanOperations.REJECT) {
          return !!selectedStatus.value;
        }
        if (selectedAction.value === groupKanbanOperations.SEND_EMAIL) {
          return !!emailMessage.value;
        }
        if (selectedAction.value === groupKanbanOperations.SEND_SMS) {
          return !!smsMessage.value;
        }
        return false;
      }),
      toggleOpen,
      selectedAction,
      groupKanbanOperations,
      selectedStatus,
      opened,
      setAction,
      emailMessage,
      smsMessage,
      notificationChannelType,
      candidateStatus,
      sendSms,
      selectedCandidates,
      sendEmail,
      localLoading,
      clearSelected,
      notifyLoading,
    };
  },
};
</script>

<style scoped lang="scss">
.kanbanGroupOperations {
  position: fixed;
  bottom: 6vh;
  display: flex;
  align-items: center;
  width: max-content;
  padding: 10px 15px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  background: #eeeeee;
  z-index: 99;
  @media screen and (max-width: 768px) {
    padding: 10px 20px;
  }
  @media screen and (max-width: 428px) {
    padding: 10px;
  }
  &__btn-list {
    @media screen and (max-width: 768px) {
      display: flex;
      width: 135px;
      overflow: scroll;
      margin-left: 135px;
      scroll-snap-type: x mandatory;
      scroll-padding: 50%;
    }
    @media screen and (max-width: 428px) {
      width: 110px;
      margin-left: 120px;
    }
    @media screen and (max-width: 427px) {
      margin-left: 0;
    }
    button {
      @media screen and (max-width: 768px) {
        scroll-snap-align: center;
        padding: 0;
        margin: 0;
        width: 135px;
      }
      @media screen and (max-width: 428px) {
        font-size: 13px;
      }
    }
  }
  &__selected {
    @media screen and (max-width: 427px) {
      display: none !important;
    }
  }
}

.custom--btn {
  background: inherit !important;
  color: #212121 !important;
  box-shadow: unset !important;

  &:first-of-type {
    margin-left: 150px;
  }
}

.counter {
  position: absolute;
  height: 100%;
  padding-right: 20px;
  border-right: 1px solid #dfdfdf;
}

.dialog {
  min-height: 463px;
  display: flex;
  flex-direction: column;
}

.kanban-group-operations__sending {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  min-height: 200px;
}

.kanban-group-operations__sending-text {
  margin: 1rem 0 0;
  max-width: 400px;
  text-align: center;
  color: #212121;
  font-size: 15px;
  line-height: 1.4;
}
</style>

<i18n locale="ru" lang="yaml">
  move: Переместить
  send_email: Отправить Email
  send_sms: Отправить SMS
  reject: Отказать
  apply: Применить
  edit: Групповое редактирование
  changeStatus: Статус
  selected: Выбрано
  cancel: Отменить
  messagesSent: Сообщения отправлены
  sendingSmsInProgress: Пожалуйста, не закрывайте окно. Идёт процесс отправки SMS…
  sendingEmailInProgress: Пожалуйста, не закрывайте окно. Идёт процесс отправки EMAIL…
  sendingMessagesInProgress: Пожалуйста, не закрывайте окно. Идёт отправка сообщений по выбранным каналам…
</i18n>

<i18n locale="en" lang="yaml">
  move: Move
  send_email: Send Email
  send_sms: Send Message
  reject: Reject
  apply: Apply
  edit: Group Edit
  changeStatus: Stage
  selected: Selected
  cancel: Cancel
  messagesSent: Messages sent
  sendingSmsInProgress: Please do not close the window. Sending SMS…
  sendingEmailInProgress: Please do not close the window. Sending EMAIL…
  sendingMessagesInProgress: Please do not close the window. Sending messages via selected channels…
</i18n>
